import React, { useState, useEffect } from 'react';
import { PALETTES } from '../marketplaceConstants.js';

function paletteIndexForItemId(id) {
  if (typeof id === 'string' && id.length) {
    let h = 0;
    for (let i = 0; i < id.length; i++) h = (h + id.charCodeAt(i) * (i + 1)) % 2147483647;
    return Math.abs(h) % PALETTES.length;
  }
  const n = Number(id);
  if (Number.isFinite(n)) return Math.abs(Math.floor(n - 1)) % PALETTES.length;
  return 0;
}

function thumbUrl(item) {
  const u = item?.coverUrl ?? item?.cover_url;
  if (typeof u === 'string' && u.trim()) return u.trim();
  const p = item?.photos;
  if (Array.isArray(p) && typeof p[0] === 'string' && p[0].trim()) return p[0].trim();
  return null;
}

function SinPortadaThumb({ bg, accent }) {
  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        background: `linear-gradient(145deg, ${bg} 0%, #1a1a22 55%, #12121a 100%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: 8,
        boxSizing: 'border-box',
      }}
    >
      <span style={{ fontSize: 11, fontWeight: 600, color: accent, letterSpacing: '0.02em', lineHeight: 1.25 }}>Sin portada</span>
    </div>
  );
}

/**
 * @param {object} item — listing / ítem del marketplace (`cover_url` / `coverUrl` del anuncio; `photos` del vendedor)
 * @param {boolean} [listingThumb] — tarjeta/lista: solo `cover_url` / `coverUrl`; la imagen encaja al hueco con `contain` y fondo difuminado (sin recortar la portada).
 */
export default function VinylCover({ item, listingThumb = false }) {
  const url = listingThumb ? (item?.coverUrl ?? item?.cover_url) : thumbUrl(item);
  const trimmed = typeof url === 'string' ? url.trim() : '';
  const [imgBroken, setImgBroken] = useState(false);

  useEffect(() => {
    setImgBroken(false);
  }, [trimmed, listingThumb]);

  if (trimmed && !imgBroken) {
    if (listingThumb) {
      return (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            overflow: 'hidden',
            background: '#0a0a0f',
          }}
        >
          <img
            src={trimmed}
            alt=""
            aria-hidden
            draggable={false}
            style={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              width: '135%',
              height: '135%',
              transform: 'translate(-50%, -50%)',
              objectFit: 'cover',
              objectPosition: 'center',
              filter: 'blur(20px)',
              opacity: 0.68,
              pointerEvents: 'none',
            }}
          />
          <img
            src={trimmed}
            alt=""
            draggable={false}
            onError={() => setImgBroken(true)}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              objectPosition: 'center',
              zIndex: 1,
            }}
          />
        </div>
      );
    }
    return (
      <img
        src={trimmed}
        alt=""
        onError={() => setImgBroken(true)}
        style={{
          display: 'block',
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'center',
          background: 'transparent',
        }}
      />
    );
  }

  if (listingThumb) {
    const [bg, accent] = PALETTES[paletteIndexForItemId(item?.id)];
    return <SinPortadaThumb bg={bg} accent={accent} />;
  }

  const [bg, accent] = PALETTES[paletteIndexForItemId(item?.id)];
  const artist = typeof item?.artist === 'string' ? item.artist : '';
  const initials = artist
    .split(/\s+/)
    .filter(Boolean)
    .map(w => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase() || '?';
  const yearLabel = item?.year != null && item?.year !== '' ? String(item.year) : '';

  return (
    <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block', width: '100%', height: '100%' }}>
      <rect width="200" height="200" fill={bg} />
      <circle cx="100" cy="100" r="92" fill="none" stroke={accent} strokeWidth="0.5" opacity="0.35" />
      <circle cx="100" cy="100" r="80" fill="#0a0a0a" opacity="0.65" />
      <circle cx="100" cy="100" r="60" fill="#070707" opacity="0.8" />
      <circle cx="100" cy="100" r="40" fill="#050505" opacity="0.85" />
      <circle cx="100" cy="100" r="26" fill={bg} />
      <circle cx="100" cy="100" r="24" fill="none" stroke={accent} strokeWidth="1" opacity="0.6" />
      <circle cx="100" cy="100" r="5" fill={accent} opacity="0.8" />
      <text x="100" y="97" textAnchor="middle" fontFamily="Georgia,serif" fontSize="10" fontWeight="bold" fill={accent} opacity="0.85">
        {initials}
      </text>
      {yearLabel ? (
        <text x="100" y="110" textAnchor="middle" fontFamily="sans-serif" fontSize="6" fill={accent} opacity="0.6">
          {yearLabel}
        </text>
      ) : null}
    </svg>
  );
}
